import React from "react";
import { Input, Tooltip } from "antd";
import { isLn1FamilyProtocol } from "./offsetMatrixHelpers";
import { LN2_MESSAGE_CATEGORIES } from "../../../flatbuffers/types/ln2MessageOptions";
import type { MappedMessageType } from "./adapterColumnTypes";
import type { Ln1KeyMappingRow } from "../../setupWizard/steps/CdoLn1MappingEditor";
import { KeyDirection } from "../../../flatbuffers/generated/archive/key-direction";
import {
  detectMatrixOverlaps,
  formatOverlapTooltip,
  intraLookup,
  categoryKeyToMcNumber,
  OVERLAP_STYLE,
  type MatrixColumnSpec,
  type CategoryRegTypeMap,
  type OverlapInfo,
} from "./offsetOverlapDetection";
import { computeColumnByteSize } from "./adapterSizeMath";

/** Minimal key-mapping shape the byte-size math needs. Exported so callers
 *  outside the component (e.g. the Review step's overlap check) can type
 *  against exactly what `buildMatrixColumnSpecs` consumes. */
export type MatrixKeyMappingSubset = ReadonlyArray<
  Pick<
    Ln1KeyMappingRow,
    | "ln2MessageKey"
    | "contentType"
    | "byte_offset"
    | "bit_offset"
    | "offset"
    | "floors_based"
    | "is_pair_secondary"
    | "direction"
  >
>;

export type AdapterOffsetValue = { initial: number; delta: number };
export type AdapterOffsetMap = Record<string, AdapterOffsetValue>;

export const BANK_LEVEL_DELTA_TOOLTIP =
  "This is a bank-level mapping and does not have a car delta";

export interface AdapterOffsetColumn {
  /** Unique key — e.g. "service" for a grouped category column, or
   * "service:normal:5" for a bank_level individual message column. */
  key: string;
  label: string;
  /** When true, the Car Delta cell for this column renders disabled with an
   * explanatory tooltip — the mapping is bank-level so a per-car delta has no
   * meaning. The Initial Offset cell remains editable. Two sources feed this
   * flag: (1) an explicit `bank_level === true` on a CDO key mapping, or
   * (2) a data-driven heuristic where every unit in the stored offsets shares
   * the same non-zero value, indicating a bank-level pattern. */
  bankLevel?: boolean;
}

export interface AdapterOffsetMatrixProps {
  protocolType: string;
  value: AdapterOffsetMap;
  onChange: (next: AdapterOffsetMap) => void;
  /**
   * Optional list of columns to render. The semantics are:
   *   - `undefined` (or omitted): use the protocol-based default column set
   *     (LN2 categories for LN1-family, COIL/DI/HR/IR for MODBUS).
   *   - explicit `[]`: render zero columns (header row only). This is how a
   *     parent indicates "no mappings configured yet" — e.g. a brand-new
   *     LN1 adapter before any key mapping has been added.
   *   - non-empty array: render exactly these columns. The parent builds this
   *     list via `extractLn1MappedTypes` (or equivalent) so the adapter matrix
   *     shows the same individual-message columns the bank matrix does — both
   *     grouped categories and bank_level individual messages.
   */
  columns?: AdapterOffsetColumn[];
  /** Mapped message types from the adapter CDO — used to derive bankLevel and
   *  isByteExpansion flags for overlap detection. */
  adapterMappedTypes?: MappedMessageType[];
  /** Key mappings from the CDO — used to compute per-category byte sizes for
   *  overlap range math. Accepts the minimal Pick subset needed. */
  ln1KeyMappings?: MatrixKeyMappingSubset;
  /** Intra-content flags keyed by MessageContent number (or JSON-coerced string
   *  key). When a unit has intra-content enabled, overlapping ranges in different
   *  categories on that unit are allowed. */
  intraContentFlags?: Partial<Record<number | string, boolean[]>>;
  /** Number of cars / lifts this adapter serves. Defaults to 2. */
  numCars?: number;
  /** Floor count — used for floor-expansion byte-size calculation. */
  floors?: number;
  /** Modbus register type map, keyed by category key. When provided, columns
   *  with different register types (e.g. COIL vs HR) are not flagged for
   *  address overlap even if their byte ranges coincide. */
  regTypes?: CategoryRegTypeMap;
  /** Called when overlap state changes — parent can use to gate save/verify. */
  onOverlapChange?: (hasOverlaps: boolean) => void;
}

const MODBUS_COLUMNS: AdapterOffsetColumn[] = [
  { key: "COIL", label: "COIL" },
  { key: "DI", label: "DI" },
  { key: "HR", label: "HR" },
  { key: "IR", label: "IR" },
];

function getDefaultColumns(protocolType: string): AdapterOffsetColumn[] {
  if (isLn1FamilyProtocol(protocolType)) {
    return LN2_MESSAGE_CATEGORIES.map((c) => ({
      key: c.categoryKey,
      label: c.label,
    }));
  }
  return MODBUS_COLUMNS;
}

function isVisibleProtocol(protocolType: string): boolean {
  if (isLn1FamilyProtocol(protocolType)) {
    return true;
  }
  return protocolType.toUpperCase() === "MODBUS";
}

function parseNumericInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "-") {
    return 0;
  }
  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return parsed;
}

/**
 * Convert a column key (e.g. "service" or "service:normal:5") into a safe
 * suffix for a `data-testid` attribute. Colons are replaced with dashes so
 * the composite key "service:normal:5" becomes "service-normal-5".
 */
function testIdSuffix(colKey: string): string {
  return colKey.toLowerCase().replace(/:/g, "-");
}

/**
 * The single LN1 map every key in one category claims, or `undefined` when they
 * do not agree.
 *
 * A column is only single-directional when every key inside it points the same
 * way. Any key at BOTH, a mix of TO_DEVICE and FROM_DEVICE, or no keys at all
 * leaves the column claiming both maps — so `undefined` is returned and overlap
 * detection treats the column exactly as it did before direction existed.
 *
 * Derived rather than authored on purpose: direction is a property of a key,
 * and letting an operator assert a column-level direction that its keys
 * contradict would suppress a real collision.
 *
 * Paired secondary rows are skipped: a pair is ONE stored key whose secondary
 * populates the lower_* group, so it carries no direction of its own.
 */
export function deriveColumnDirection(
  keyMappings: MatrixKeyMappingSubset | undefined,
  categoryKey: string,
): KeyDirection | undefined {
  if (!keyMappings || keyMappings.length === 0) return undefined;

  // A column key is either a whole category ("control") or one broken-out
  // message ("control:normal:14"), and a mapping row is always keyed the
  // composite way. Comparing `split(":")[0]` — the category segment — against a
  // composite column key matched NOTHING, so every per-message column derived
  // `undefined` and could never be excused as direction-split. That silently
  // defeated the feature exactly where it matters: the Swift CEC byte-8 pair is
  // two per-message columns.
  const rows = keyMappings.filter((row) => {
    if (row.is_pair_secondary) return false;
    if (row.ln2MessageKey === categoryKey) return true;
    return row.ln2MessageKey.split(":")[0] === categoryKey;
  });
  if (rows.length === 0) return undefined;

  const first = rows[0]?.direction;
  if (first === undefined || first === KeyDirection.BOTH) return undefined;

  return rows.every((row) => row.direction === first) ? first : undefined;
}

/**
 * Build MatrixColumnSpec[] from columns + mapped-type metadata. Exported and
 * pure so the Review step can compute the *same* specs the matrix renders —
 * in particular the real per-category byte `size`, which a previous Review
 * implementation hardcoded to 1 and thereby flagged phantom overlaps.
 */
export function buildMatrixColumnSpecs(
  effectiveColumns: AdapterOffsetColumn[],
  adapterMappedTypes: MappedMessageType[] | undefined,
  ln1KeyMappings: MatrixKeyMappingSubset | undefined,
  floors: number | undefined,
  regTypes: CategoryRegTypeMap | undefined,
): MatrixColumnSpec[] {
  if (!adapterMappedTypes || adapterMappedTypes.length === 0) return [];
  return effectiveColumns.map((col) => {
    const mt = adapterMappedTypes.find((t) => t.categoryKey === col.key);
    const size = computeColumnByteSize(
      ln1KeyMappings ?? [],
      col.key,
      categoryKeyToMcNumber(col.key),
      floors ?? 0,
    );
    return {
      colKey: col.key,
      label: col.label,
      size: Math.max(1, size),
      mcNumber: mt ? categoryKeyToMcNumber(col.key) : 0,
      bankLevel: mt?.bankLevel === true,
      isByteExpansion: mt?.isByteExpansion === true,
      groupShared: mt?.groupShared,
      regType: regTypes?.[col.key],
      direction: deriveColumnDirection(ln1KeyMappings, col.key),
    };
  });
}

/**
 * Everything overlap detection needs from an adapter form.
 *
 * Deliberately a subset of `AdapterOffsetMatrixProps` with the callbacks
 * removed: the Overlap Analysis report is computed from exactly the values the
 * matrix renders, and giving that computation its own input type means a caller
 * cannot accidentally feed it a different column set or a different car count
 * from the one painting the red cells beside it.
 */
export interface AdapterOverlapInput {
  /** Vendor protocol, which decides the default column set. */
  protocolType: string;
  /** The offset map as edited, not as saved. */
  value: AdapterOffsetMap;
  /** Explicit columns; `undefined` falls back to the protocol defaults. */
  columns?: AdapterOffsetColumn[];
  /** Mapped message types from the adapter CDO. */
  adapterMappedTypes?: MappedMessageType[];
  /** Key mappings, for per-category byte sizes. */
  ln1KeyMappings?: MatrixKeyMappingSubset;
  /** Intra-content flags keyed by MessageContent number. */
  intraContentFlags?: Partial<Record<number | string, boolean[]>>;
  /** Cars this adapter serves. Defaults to 2, exactly as the matrix does. */
  numCars?: number;
  /** Floor count, for floor-expansion byte sizes. */
  floors?: number;
  /** Modbus register-type map, keyed by category key. */
  regTypes?: CategoryRegTypeMap;
}

/** One overlap run, together with the scope it actually covered. */
export interface AdapterOverlapAnalysis {
  /** The raw detection map, keyed `${colKey}:${unitIdx}` and double-filed. */
  overlaps: Map<string, OverlapInfo[]>;
  /** The column specs the run compared. Its length is what "checked" means. */
  columnSpecs: MatrixColumnSpec[];
  /** The car count the run used, after the default was applied. */
  numCars: number;
}

/**
 * One overlap run, shared by the matrix's red cells and the Overlap Analysis
 * report.
 *
 * The defaults live INSIDE this function on purpose. `columns ?? defaults` and
 * `numCars ?? 2` used to be applied at the call site, so any second caller had
 * to remember to apply them identically — and one already does not:
 * `StepReview` floors the car count at 2 instead. A caller that forgets a
 * default silently analyses a different adapter from the one on screen, which
 * is the whole class of defect the report exists to remove.
 */
export function computeAdapterOverlaps(input: AdapterOverlapInput): AdapterOverlapAnalysis {
  const effectiveColumns: AdapterOffsetColumn[] =
    input.columns !== undefined ? input.columns : getDefaultColumns(input.protocolType);
  const columnSpecs = buildMatrixColumnSpecs(
    effectiveColumns,
    input.adapterMappedTypes,
    input.ln1KeyMappings,
    input.floors,
    input.regTypes,
  );
  const numCars = input.numCars ?? 2;
  const overlaps = detectMatrixOverlaps(
    columnSpecs,
    input.value,
    numCars,
    (mcN: number, unitIdx: number) => intraLookup(input.intraContentFlags, mcN, unitIdx),
    input.regTypes,
    // Lets the detector refine a range overlap down to the bytes the columns'
    // messages actually claim, instead of trusting the bounding box. The bank
    // matrix is given the same two, so the two screens refine alike.
    input.ln1KeyMappings,
    input.floors,
  );
  return { overlaps, columnSpecs, numCars };
}

export default function AdapterOffsetMatrix({
  protocolType,
  value,
  onChange,
  columns = undefined,
  adapterMappedTypes = undefined,
  ln1KeyMappings = undefined,
  intraContentFlags = undefined,
  numCars = undefined,
  floors = undefined,
  regTypes = undefined,
  onOverlapChange = undefined,
}: AdapterOffsetMatrixProps): React.ReactElement | null {
  if (!isVisibleProtocol(protocolType)) {
    return null;
  }

  // When the parent passes `columns` (even as an empty array) it is the
  // source of truth — an explicit `[]` renders zero columns. Only fall back
  // to protocol defaults when the prop was omitted entirely (undefined).
  const effectiveColumns: AdapterOffsetColumn[] =
    columns !== undefined ? columns : getDefaultColumns(protocolType);

  // The specs, the car-count default and the detection run all live in
  // computeAdapterOverlaps, so the Overlap Analysis report is literally the
  // same call rather than a second implementation that agrees for now.
  // Column specs are only non-empty when adapterMappedTypes is provided —
  // otherwise the overlap map stays empty and no red cells are rendered.
  const overlapMap = React.useMemo(
    () =>
      computeAdapterOverlaps({
        protocolType,
        value,
        columns: effectiveColumns,
        adapterMappedTypes,
        ln1KeyMappings,
        intraContentFlags,
        numCars,
        floors,
        regTypes,
      }).overlaps,
    [
      protocolType,
      value,
      effectiveColumns,
      adapterMappedTypes,
      ln1KeyMappings,
      intraContentFlags,
      numCars,
      floors,
      regTypes,
    ],
  );

  /**
   * Every flagged overlap, regrouped by column and split into the two rows this
   * table actually renders.
   *
   * `detectMatrixOverlaps` files collisions under `${colKey}:${unitIdx}` for
   * EVERY car, but the table has only two body rows. It used to look up exactly
   * `${col.key}:0` and `${col.key}:1`, so on a six-car bank an overlap on cars
   * 3..6 set `hasMatrixOverlaps` — which blocks verification with "offset matrix
   * has overlapping registers" — while no cell turned red. The operator was told
   * to fix something the screen refused to point at.
   *
   * The delta row owns cars 1..N-1, because one delta value generates all of
   * them: an overlap on any of those is the delta's fault and is fixed by
   * editing that one cell. The initial row owns car 0 alone.
   *
   * A shared register (bank-level, group-shared, or any column with a car delta
   * of 0) is filed under `SHARED_UNIT`. It belongs to the INITIAL row: it has no
   * per-car delta at all, and on a bank-level column the delta input is
   * disabled, so lighting that cell red would point the operator at a control
   * they cannot use.
   */
  const overlapRows = React.useMemo(() => {
    const rows = new Map<string, { initial: OverlapInfo[]; delta: OverlapInfo[] }>();
    overlapMap.forEach((infos, key) => {
      const sep = key.lastIndexOf(":");
      const colKey = key.slice(0, sep);
      const unit = Number(key.slice(sep + 1));
      const entry = rows.get(colKey) ?? { initial: [], delta: [] };
      if (unit <= 0) {
        entry.initial.push(...infos);
      } else {
        entry.delta.push(...infos);
      }
      rows.set(colKey, entry);
    });

    return rows;
  }, [overlapMap]);

  // Notify parent when overlap state changes so they can gate save/verify.
  React.useEffect(() => {
    if (onOverlapChange) {
      onOverlapChange(overlapMap.size > 0);
    }
  }, [overlapMap.size, onOverlapChange]);

  function handleChange(colKey: string, row: "initial" | "delta", raw: string): void {
    if (row === "delta") {
      const col = effectiveColumns.find((c) => c.key === colKey);
      if (col?.bankLevel) {
        return;
      }
    }
    const parsed = parseNumericInput(raw);
    if (parsed === null) {
      return;
    }
    const current = value[colKey] ?? { initial: 0, delta: 0 };
    const updated: AdapterOffsetValue =
      row === "initial"
        ? { initial: parsed, delta: current.delta }
        : { initial: current.initial, delta: parsed };
    onChange({ ...value, [colKey]: updated });
  }

  return (
    <div data-testid="adapter-offset-matrix" style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "4px 8px", whiteSpace: "nowrap" }}>Row</th>
            {effectiveColumns.map((col) => (
              <th key={col.key} style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: "4px 8px", whiteSpace: "nowrap", fontWeight: 500 }}>
              Initial Offset
            </td>
            {effectiveColumns.map((col) => {
              // The initial-offset cell owns car 0 only; cars 1..N-1 belong to the
              // delta row below, so between them the two rows cover every unit
              // detectMatrixOverlaps can flag.
              const overlaps = overlapRows.get(col.key)?.initial ?? [];
              const hasOverlap = overlaps.length > 0;
              const overlapTooltipText = hasOverlap ? formatOverlapTooltip(overlaps[0]) : "";

              // Always render the same Tooltip>span>Input tree regardless of overlap
              // status. Conditionally adding/removing the Tooltip wrapper causes React
              // to unmount and remount the Input when overlap status changes mid-type,
              // stealing keyboard focus from the active cell.
              return (
                <td key={col.key} style={{ padding: "4px 8px" }}>
                  <Tooltip title={overlapTooltipText}>
                    <span style={{ display: "inline-block" }}>
                      <Input
                        data-testid={`adapter-offset-initial-${testIdSuffix(col.key)}`}
                        value={String(value[col.key]?.initial ?? 0)}
                        onChange={(e) => handleChange(col.key, "initial", e.target.value)}
                        style={hasOverlap ? { width: 80, ...OVERLAP_STYLE } : { width: 80 }}
                        size="small"
                      />
                    </span>
                  </Tooltip>
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={{ padding: "4px 8px", whiteSpace: "nowrap", fontWeight: 500 }}>Car Delta</td>
            {effectiveColumns.map((col) => {
              // The delta row owns EVERY car past the first, not just car 1. One
              // delta generates all of them, so an overlap on any is fixed here —
              // and looking up only car 1 is what let a six-car bank block its own
              // save with nothing highlighted.
              const deltaOverlaps = overlapRows.get(col.key)?.delta ?? [];
              const hasDeltaOverlap = deltaOverlaps.length > 0;
              const deltaOverlapTooltipText = hasDeltaOverlap
                ? formatOverlapTooltip(deltaOverlaps[0])
                : "";

              // Resolve tooltip: bank-level wins, then overlap, then empty (no tooltip shown).
              // Always render the same Tooltip>span>Input tree so toggling bankLevel or
              // overlap status never unmounts the Input and steals keyboard focus.
              let deltaTitle = "";
              if (col.bankLevel === true) {
                deltaTitle = BANK_LEVEL_DELTA_TOOLTIP;
              } else if (hasDeltaOverlap) {
                deltaTitle = deltaOverlapTooltipText;
              }

              return (
                <td key={col.key} style={{ padding: "4px 8px" }}>
                  <Tooltip title={deltaTitle}>
                    <span style={{ display: "inline-block" }}>
                      <Input
                        data-testid={`adapter-offset-delta-${testIdSuffix(col.key)}`}
                        value={String(value[col.key]?.delta ?? 0)}
                        onChange={(e) => handleChange(col.key, "delta", e.target.value)}
                        style={hasDeltaOverlap ? { width: 80, ...OVERLAP_STYLE } : { width: 80 }}
                        size="small"
                        disabled={col.bankLevel === true}
                      />
                    </span>
                  </Tooltip>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
